import { CheckCircle, Users } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import type React from "react";
import { useMemo } from "react";
import { Label, Text, XStack } from "tamagui";
import { Block } from "../../ui/Block";
import { Body } from "../../ui/Body";
import { Checkbox } from "../../ui/Checkbox";
import { Group } from "../../ui/Group";
import type { InfoRowItemProps } from "../../ui/InfoRow";
import type BaseModuleModel from "../core/BaseModuleModel";
import type { RatingManager } from "./rating/RatingManager";
import { RatingsInput } from "./rating/RatingUI";

// biome-ignore lint/suspicious/noExplicitAny: Because
type Module = BaseModuleModel<any, any> & { waitingForUsers: string[] };

type DataItemProps = {
  value: string;
  text: string;
  users?: string[];
  // biome-ignore lint/suspicious/noExplicitAny: Because
  ratings?: RatingManager<any, any>;
  dataUid?: string;
  active?: boolean;
  module: Module;
  onItemPress: (date: string) => void;
  getInfoRowItems?: (
    props: Pick<
      DataItemProps,
      "value" | "text" | "users" | "ratings" | "dataUid" | "active" | "module"
    >,
  ) => InfoRowItemProps[];
};

const DataItem: React.FC<DataItemProps> = observer(
  ({
    value,
    text,
    users,
    ratings,
    dataUid,
    module,
    active,
    onItemPress,
    getInfoRowItems,
  }) => {
    const isSelected = module.data.selectedDates?.includes(value);

    const infoRowItems = useMemo<InfoRowItemProps[] | undefined>(() => {
      if (getInfoRowItems) {
        return getInfoRowItems({
          value,
          text,
          users,
          ratings,
          dataUid,
          module,
          active,
        });
      }
      if (!users) {
        return undefined;
      }

      const items = [
        {
          icon: Users,
          text: `${users.length} of ${module.plan.data.userIds.length} participants`,
        },
      ];

      if (module.isDone && isSelected) {
        items.push({
          icon: CheckCircle,
          text: "Decided",
        });
      }

      return items;
    }, [
      users,
      isSelected,
      module.isDone,
      module.plan.data.userIds,
      getInfoRowItems,
      value,
      text,
      ratings,
      dataUid,
      module,
      active,
    ]);

    let ratingsArea = null;
    if (ratings && dataUid) {
      ratingsArea = (
        <RatingsInput
          withAverage
          dataUid={dataUid}
          ratings={ratings}
          disabled
          size={24}
        />
      );
    }

    return (
      <Block
        onPress={() => onItemPress(value)}
        icon={<Checkbox checked={!!active} />}
        infoRowItems={infoRowItems}
        grouped
        opacity={module.isDone && !isSelected ? 0.3 : 1}
      >
        <XStack width={"100%"} gap="$medium">
          <Text gap="$medium" flex={1}>
            <Label>{text}</Label>
            {users ? <Body>{Array.from(users).join(", ")}</Body> : undefined}
          </Text>
          {ratingsArea}
        </XStack>
      </Block>
    );
  },
);

export const DataItemsSelect: React.FC<{
  module: Module;
  onItemPress: (value: string) => void;
  activeItems: string[];
  items: {
    // biome-ignore lint/suspicious/noExplicitAny: Because
    ratings?: RatingManager<any, any>;
    users?: string[];
    value: string;
    text: string;
  }[];
  getInfoRowItems?: DataItemProps["getInfoRowItems"];
}> = observer(
  ({ module, onItemPress, activeItems, items, getInfoRowItems }) => {
    const _isWaitingForUsers = !!module.waitingForUsers.length;

    return (
      <Group borderTop={false}>
        {items.map(({ value, text, users, ratings }) => (
          <DataItem
            key={value}
            text={text}
            value={value}
            users={users}
            dataUid={value}
            ratings={ratings}
            module={module}
            active={activeItems?.includes(value)}
            onItemPress={onItemPress}
            getInfoRowItems={getInfoRowItems}
          />
        ))}
      </Group>
    );
  },
);
